/**
 * Single source of truth for money display across the designer portal.
 *
 * The symbol + locale are now DRIVEN BY THE BUSINESS'S SETTING (currency +
 * country), so a bakery in the US sees "$1,250.00" and a shop in the UK sees
 * "£1,250.00" — not hardcoded rupees. Everything (Analytics' fmt(), Payments'
 * formatRs()/formatINR(), P&L, Expenses, Goals…) delegates here, so this stays a
 * one-file change.
 *
 * Bootstrap: setCurrency({symbol, locale, code, country}) is called once at
 * login from GET /api/designer/locale/. We also cache it in localStorage so a
 * hard reload shows the right currency immediately (before the fetch returns).
 */

const DEFAULTS = { symbol: '₹', locale: 'en-IN', code: 'INR', country: 'IN' };

const _read = () => {
  try {
    const raw = localStorage.getItem('vasstr_currency');
    if (raw) return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return { ...DEFAULTS };
};

let _cur = _read();

/** Update the active currency/locale (call once at login, persists for reloads). */
export const setCurrency = ({ symbol, locale, code, country } = {}) => {
  _cur = {
    symbol: symbol || _cur.symbol || DEFAULTS.symbol,
    locale: locale || _cur.locale || DEFAULTS.locale,
    code: code || _cur.code || DEFAULTS.code,
    country: (country || _cur.country || DEFAULTS.country),
  };
  try { localStorage.setItem('vasstr_currency', JSON.stringify(_cur)); } catch { /* ignore */ }
};

export const getCurrency = () => ({ ..._cur });
/** True only for India — gates GST/GSTR-1/UPI/festival UI. */
export const isIndia = () => String(_cur.country || 'IN').toUpperCase().startsWith('IN');

// Back-compat exports. These are read at import time; prefer the format* fns
// (which read the live value) for anything that must reflect a currency change.
export const CURRENCY_SYMBOL = _cur.symbol;
export const CURRENCY_LOCALE = _cur.locale;

/** Number part only, locale-grouped: 125000 → "1,25,000" (en-IN) / "125,000" (en-US). */
export const formatAmount = (val, { maxDecimals = 2 } = {}) => {
  const n = Number(val || 0);
  if (!Number.isFinite(n)) return '0';
  try {
    return new Intl.NumberFormat(_cur.locale, { maximumFractionDigits: maxDecimals }).format(n);
  } catch {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: maxDecimals }).format(n);
  }
};

/** Full money string with the business's symbol: 125000 → "₹1,25,000" / "$125,000". */
export const formatMoney = (val, opts) => `${_cur.symbol}${formatAmount(val, opts)}`;

/** Whole-unit variant (dashboards): 1234.6 → "₹1,235" / "$1,235". */
export const formatMoneyWhole = (val) => `${_cur.symbol}${formatAmount(Math.round(Number(val || 0)), { maxDecimals: 0 })}`;
